import React, { useEffect, useRef, useState } from 'react';

function NuclearExplosionOverlay({ onComplete }) {
  const canvasRef = useRef(null);
  const audioRefs = useRef([]);
  const [phase, setPhase] = useState(0); // 0=flash, 1=fireball, 2=mushroom, 3=shockwave, 4=aftermath
  const particlesRef = useRef([]);
  const frameRef = useRef(null);
  const startTimeRef = useRef(Date.now());

  // Play NukeExplosion.mp3 back to back to back to back (4 overlapping instances)
  useEffect(() => {
    const audioFiles = [];
    const delays = [0, 300, 700, 1200, 2000, 3000, 4000, 5000];

    delays.forEach((delay) => {
      const timeout = setTimeout(() => {
        const audio = new Audio('/NukeExplosion.mp3');
        audio.volume = 1.0;
        audio.play().catch(() => {});
        audioFiles.push(audio);
      }, delay);
      audioFiles.push(timeout);
    });

    audioRefs.current = audioFiles;

    return () => {
      audioRefs.current.forEach(ref => {
        if (typeof ref === 'number') clearTimeout(ref);
        else if (ref && ref.pause) { ref.pause(); ref.currentTime = 0; }
      });
    };
  }, []);

  // Phase progression
  useEffect(() => {
    const timers = [
      setTimeout(() => setPhase(1), 200),   // Flash -> Fireball
      setTimeout(() => setPhase(2), 800),   // Fireball -> Mushroom
      setTimeout(() => setPhase(3), 2000),  // Mushroom -> Shockwave
      setTimeout(() => setPhase(4), 3500),  // Shockwave -> Aftermath
      setTimeout(() => {
        if (onComplete) onComplete();
      }, 7000), // End after 7 seconds
    ];
    return () => timers.forEach(clearTimeout);
  }, [onComplete]);

  // Canvas particle/explosion rendering
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    function resize() {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    }
    resize();
    window.addEventListener('resize', resize);

    // Initialize particles
    for (let i = 0; i < 300; i++) {
      particlesRef.current.push({
        x: canvas.width / 2,
        y: canvas.height / 2,
        vx: (Math.random() - 0.5) * 30,
        vy: (Math.random() - 0.5) * 30,
        size: Math.random() * 8 + 2,
        color: ['#FF0000', '#FF4400', '#FF8800', '#FFFF00', '#00FF00', '#FFFFFF'][Math.floor(Math.random() * 6)],
        life: 1,
        decay: Math.random() * 0.008 + 0.003,
        type: Math.random() > 0.7 ? 'ember' : 'debris',
      });
    }

    function drawFrame() {
      const elapsed = Date.now() - startTimeRef.current;
      const w = canvas.width;
      const h = canvas.height;

      // Semi-transparent clear for trails
      ctx.globalCompositeOperation = 'source-over';
      ctx.fillStyle = 'rgba(0, 0, 0, 0.15)';
      ctx.fillRect(0, 0, w, h);

      // Screen shake effect via CSS transform is handled separately

      // Draw expanding shockwave rings
      if (elapsed > 300) {
        const ringCount = 5;
        for (let i = 0; i < ringCount; i++) {
          const ringTime = elapsed - 300 - i * 200;
          if (ringTime > 0) {
            const radius = ringTime * 1.5;
            const alpha = Math.max(0, 1 - ringTime / 2000);
            ctx.beginPath();
            ctx.arc(w / 2, h / 2, radius, 0, Math.PI * 2);
            ctx.strokeStyle = `rgba(255, ${100 + i * 30}, 0, ${alpha})`;
            ctx.lineWidth = 8 - i;
            ctx.stroke();
          }
        }
      }

      // Draw fireball
      if (elapsed > 150 && elapsed < 4000) {
        const fireballAge = elapsed - 150;
        const maxRadius = Math.min(w, h) * 0.6;
        const radius = Math.min(maxRadius, fireballAge * 0.8);
        const alpha = Math.max(0, 1 - fireballAge / 4000);

        const gradient = ctx.createRadialGradient(w / 2, h / 2, 0, w / 2, h / 2, radius);
        gradient.addColorStop(0, `rgba(255, 255, 255, ${alpha})`);
        gradient.addColorStop(0.2, `rgba(255, 255, 0, ${alpha * 0.9})`);
        gradient.addColorStop(0.4, `rgba(255, 140, 0, ${alpha * 0.8})`);
        gradient.addColorStop(0.6, `rgba(255, 50, 0, ${alpha * 0.6})`);
        gradient.addColorStop(0.8, `rgba(200, 0, 0, ${alpha * 0.3})`);
        gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');

        ctx.globalCompositeOperation = 'lighter';
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, w, h);
      }

      // Draw mushroom cloud column
      if (elapsed > 800 && elapsed < 6000) {
        const cloudAge = elapsed - 800;
        const columnWidth = 80 + cloudAge * 0.03;
        const cloudTop = Math.max(h * 0.1, h / 2 - cloudAge * 0.2);
        const alpha = Math.max(0, 1 - cloudAge / 6000);

        ctx.globalCompositeOperation = 'lighter';
        // Column
        const colGrad = ctx.createLinearGradient(w / 2 - columnWidth, 0, w / 2 + columnWidth, 0);
        colGrad.addColorStop(0, 'rgba(0,0,0,0)');
        colGrad.addColorStop(0.3, `rgba(255, 100, 0, ${alpha * 0.5})`);
        colGrad.addColorStop(0.5, `rgba(255, 200, 0, ${alpha * 0.7})`);
        colGrad.addColorStop(0.7, `rgba(255, 100, 0, ${alpha * 0.5})`);
        colGrad.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = colGrad;
        ctx.fillRect(w / 2 - columnWidth, cloudTop, columnWidth * 2, h / 2 - cloudTop + 100);

        // Mushroom cap
        const capRadius = 100 + cloudAge * 0.15;
        const capGrad = ctx.createRadialGradient(w / 2, cloudTop, 0, w / 2, cloudTop, capRadius);
        capGrad.addColorStop(0, `rgba(255, 255, 200, ${alpha})`);
        capGrad.addColorStop(0.3, `rgba(255, 180, 0, ${alpha * 0.8})`);
        capGrad.addColorStop(0.6, `rgba(255, 80, 0, ${alpha * 0.5})`);
        capGrad.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = capGrad;
        ctx.beginPath();
        ctx.arc(w / 2, cloudTop, capRadius, 0, Math.PI * 2);
        ctx.fill();
      }

      // Update and draw particles
      ctx.globalCompositeOperation = 'lighter';
      particlesRef.current.forEach(p => {
        p.x += p.vx;
        p.y += p.vy;
        p.vy += 0.05; // gravity
        p.life -= p.decay;
        if (p.life <= 0) {
          // Respawn from center with new velocity
          p.x = w / 2 + (Math.random() - 0.5) * 100;
          p.y = h / 2 + (Math.random() - 0.5) * 100;
          p.vx = (Math.random() - 0.5) * 25;
          p.vy = (Math.random() - 0.5) * 25 - 5;
          p.life = 1;
        }

        const alpha = Math.max(0, p.life);
        if (p.type === 'ember') {
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
          ctx.fillStyle = p.color.replace(')', `, ${alpha})`).replace('rgb', 'rgba').replace('#', '');
          // Handle hex colors
          const r = parseInt(p.color.slice(1, 3), 16);
          const g = parseInt(p.color.slice(3, 5), 16);
          const b = parseInt(p.color.slice(5, 7), 16);
          ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${alpha})`;
          ctx.fill();
        } else {
          const r = parseInt(p.color.slice(1, 3), 16);
          const g = parseInt(p.color.slice(3, 5), 16);
          const b = parseInt(p.color.slice(5, 7), 16);
          ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${alpha})`;
          ctx.fillRect(p.x, p.y, p.size, p.size);
        }
      });

      // Nuclear radiation symbols flying outward
      if (elapsed > 500 && elapsed < 5000) {
        ctx.globalCompositeOperation = 'source-over';
        const symbolCount = 12;
        for (let i = 0; i < symbolCount; i++) {
          const angle = (i / symbolCount) * Math.PI * 2 + elapsed * 0.001;
          const dist = ((elapsed - 500) * 0.3) % (Math.max(w, h) * 0.8);
          const sx = w / 2 + Math.cos(angle) * dist;
          const sy = h / 2 + Math.sin(angle) * dist;
          const alpha = Math.max(0, 1 - dist / (Math.max(w, h) * 0.7));
          ctx.font = `${30 + Math.sin(elapsed * 0.01 + i) * 10}px sans-serif`;
          ctx.fillStyle = `rgba(0, 255, 0, ${alpha})`;
          ctx.textAlign = 'center';
          ctx.fillText('☢️', sx, sy);
        }
      }

      frameRef.current = requestAnimationFrame(drawFrame);
    }

    drawFrame();

    return () => {
      window.removeEventListener('resize', resize);
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
    };
  }, []);

  return (
    <div className="nuclear-explosion-overlay" data-phase={phase}>
      <canvas ref={canvasRef} className="nuclear-explosion-canvas" />

      {/* INITIAL WHITE FLASH */}
      <div className={`nuclear-flash ${phase >= 0 ? 'active' : ''}`} />

      {/* Screen border radiation */}
      <div className="nuclear-radiation-border" />

      {/* ☢️ NUCLEAR ☢️ text */}
      <div className="nuclear-text-container">
        <div className="nuclear-mega-text">☢️ NUCLEAR ☢️</div>
        <div className="nuclear-sub-text">MELTDOWN</div>
      </div>

      {/* Scanlines for extra chaos */}
      <div className="nuclear-scanlines" />

      {/* Screen crack effect */}
      <div className="nuclear-cracks" />
    </div>
  );
}

export default NuclearExplosionOverlay;
